import { Quat, Vec3 } from 'playcanvas';

import { CameraMotionController, clearCameraMotionLookThrough, type CameraMotionTargetSet, type CameraMotionTrackData } from './camera-motion-controller';
import { ElementType } from './element';
import { Events } from './events';
import { HelperPoint } from './helper-point';
import { Scene } from './scene';

type DocVec3 = [number, number, number];
type DocQuat = [number, number, number, number];

type CameraMotionDocBundleV0 = {
    version: 0;

    root: {
        name: string;
        visible: boolean;
        position: DocVec3;
        rotation: DocQuat;
        scale: DocVec3;
    };

    body: {
        name: string;
        visible: boolean;
    };

    track: CameraMotionTrackData;
};

const pack3 = (v: Vec3): DocVec3 => [v.x, v.y, v.z];
const pack4 = (q: Quat): DocQuat => [q.x, q.y, q.z, q.w];

const cloneTrack = (t: CameraMotionTrackData): CameraMotionTrackData => ({
    frames: [...t.frames],
    positions: [...t.positions],
    targets: [...t.targets]
});

const safeString = (v: unknown, fallback: string) => {
    const s = typeof v === 'string' ? v : String(v ?? '');
    return s.trim() ? s : fallback;
};

const safeBool = (v: unknown, fallback = true) => {
    return (typeof v === 'boolean') ? v : fallback;
};

const hasVec3Tuple = (v: unknown): v is DocVec3 => {
    return (
        Array.isArray(v) &&
        v.length === 3 &&
        Number.isFinite(v[0]) &&
        Number.isFinite(v[1]) &&
        Number.isFinite(v[2])
    );
};

const hasQuatTuple = (v: unknown): v is DocQuat => {
    return (
        Array.isArray(v) &&
        v.length === 4 &&
        Number.isFinite(v[0]) &&
        Number.isFinite(v[1]) &&
        Number.isFinite(v[2]) &&
        Number.isFinite(v[3])
    );
};

const isFiniteNumberArray = (v: unknown): v is number[] => {
    return Array.isArray(v) && v.every((n) => typeof n === 'number' && Number.isFinite(n));
};

const isStrictlyIncreasing = (values: number[]) => {
    for (let i = 1; i < values.length; i++) {
        if (values[i] <= values[i - 1]) {
            return false;
        }
    }

    return true;
};

const isTrack = (v: any): v is CameraMotionTrackData => {
    if (!v || typeof v !== 'object') {
        return false;
    }

    if (!isFiniteNumberArray(v.frames) || !isFiniteNumberArray(v.positions) || !isFiniteNumberArray(v.targets)) {
        return false;
    }

    if (v.frames.length < 1) {
        return false;
    }

    if (!isStrictlyIncreasing(v.frames)) {
        return false;
    }

    const expectedVecLength = v.frames.length * 3;
    if (v.positions.length !== expectedVecLength || v.targets.length !== expectedVecLength) {
        return false;
    }

    return true;
};

const isRootBundleShape = (v: unknown): v is CameraMotionDocBundleV0['root'] => {
    if (!v || typeof v !== 'object') {
        return false;
    }

    const root = v as Record<string, unknown>;

    return (
        typeof root.name === 'string' &&
        typeof root.visible === 'boolean' &&
        hasVec3Tuple(root.position) &&
        hasQuatTuple(root.rotation) &&
        hasVec3Tuple(root.scale)
    );
};

const isBodyBundleShape = (v: unknown): v is CameraMotionDocBundleV0['body'] => {
    if (!v || typeof v !== 'object') {
        return false;
    }

    const body = v as Record<string, unknown>;

    return (
        typeof body.name === 'string' &&
        typeof body.visible === 'boolean'
    );
};

const isDocBundleV0 = (v: any): v is CameraMotionDocBundleV0 => {
    return (
        v && typeof v === 'object' &&
        v.version === 0 &&
        isRootBundleShape(v.root) &&
        isBodyBundleShape(v.body) &&
        isTrack(v.track)
    );
};

export const registerCameraMotionDocEvents = (scene: Scene, events: Events) => {
    // Cross-system: scene camera fires this when activating its look-through
    events.on('cameraMotion.clearLookThrough', () => {
        clearCameraMotionLookThrough();
    });

    events.function('docSerialize.cameraMotion', (): CameraMotionDocBundleV0[] => {
        const helpers = scene.getElementsByType(ElementType.helper) as HelperPoint[];
        const bundles: CameraMotionDocBundleV0[] = [];

        for (const helper of helpers) {
            if (!helper || helper.type !== ElementType.helper) continue;

            // Only serialize camera motion roots with attached controller + body helper.
            const controller = (helper as any).cameraMotionController as CameraMotionController | undefined;
            const animatedHelpers = (helper as any).cameraMotionAnimatedHelpers as { body?: HelperPoint } | undefined;
            if (!controller || !animatedHelpers?.body) continue;

            const track = controller.getTrack();
            const rootPos = helper.entity.getLocalPosition();
            const rootRot = helper.entity.getLocalRotation();
            const rootScale = helper.entity.getLocalScale();

            bundles.push({
                version: 0,
                root: {
                    name: safeString(helper.name, 'cam_root'),
                    visible: !!helper.visible,
                    position: pack3(rootPos),
                    rotation: pack4(rootRot),
                    scale: pack3(rootScale)
                },
                body: {
                    name: safeString(animatedHelpers.body.name, 'cam_body'),
                    visible: !!animatedHelpers.body.visible
                },
                track: cloneTrack(track)
            });
        }

        return bundles;
    });

    events.function('docDeserialize.cameraMotion', async (data: any) => {
        if (!Array.isArray(data) || data.length < 1) return;

        for (const raw of data) {
            if (!isDocBundleV0(raw)) continue;

            const rootName = safeString(raw.root?.name, 'cam_root');
            const base = rootName.replace(/_root$/, '');

            const root = new HelperPoint({
                name: rootName,
                animated: false,
                visible: safeBool(raw.root?.visible, true)
            });

            if (hasVec3Tuple(raw.root?.position)) {
                root.entity.setLocalPosition(new Vec3(raw.root.position));
            }
            if (hasQuatTuple(raw.root?.rotation)) {
                root.entity.setLocalRotation(new Quat(raw.root.rotation));
            }
            if (hasVec3Tuple(raw.root?.scale)) {
                root.entity.setLocalScale(new Vec3(raw.root.scale));
            }

            await scene.add(root);

            const body = new HelperPoint({
                name: safeString(raw.body?.name, `${base}_body`),
                animated: true,
                visible: safeBool(raw.body?.visible, true),
                parentEntity: root.entity
            });

            body.parentElement = root;
            root.childElements.push(body);
            await scene.add(body);

            const targets: CameraMotionTargetSet = {
                body: body.entity
            };

            const controller = new CameraMotionController(events, targets, cloneTrack(raw.track));
            (root as any).cameraMotionController = controller;
            (root as any).cameraMotionAnimatedHelpers = { body };

            scene.forceRender = true;
            events.fire('hierarchy.changed');
        }
    });
};
